import { useState } from 'react';
import { formatJMD } from '../../lib/finances';
import { sellThrough, unpaidDue } from '../../lib/stockist';

// Closing a consignment out.
//
// A stockist invoice ends when every unit is accounted for, and there are
// only two ways a unit can end: it sold, or it came back. This is the step
// that resolves the difference — you say how many were returned, and anything
// still unaccounted for stays open rather than being quietly written off.
//
// Returned units go back onto your own shelf, because they physically did.
export default function CloseOutDialog({ invoice, statements, onSubmit, onCancel }) {
  const rows = sellThrough(invoice, statements);
  const outstanding = unpaidDue(statements);

  const [returns, setReturns] = useState(() => {
    const seed = {};
    for (const row of rows) {
      const already = Number(row.returned) || 0;
      if (already > 0) seed[row.itemId] = String(already);
    }
    return seed;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // What each line looks like given the returns being typed right now.
  const preview = rows.map((row) => {
    const returned = Number(returns[row.itemId]) || 0;
    const resolvable = row.sent - row.soldToDate; // the most that can come back
    return {
      ...row,
      newReturned: returned,
      // Delta is what actually moves stock: the field holds a running total,
      // not "how many came back today", so re-opening this dialog and saving
      // again must not add the same units to your shelf twice.
      delta: returned - row.returned,
      stillOut: Math.max(0, row.sent - row.soldToDate - returned),
      tooMany: returned > resolvable,
    };
  });

  const stillOut = preview.reduce((sum, row) => sum + row.stillOut, 0);
  const invalid = preview.some((row) => row.tooMany);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (invalid) {
      setError("You can't return more than was left after sales.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(
        Object.fromEntries(preview.map((row) => [row.itemId, row.newReturned])),
        preview.filter((row) => row.delta !== 0).map((row) => ({
          productId: row.productId,
          quantity: row.delta,
        }))
      );
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Close out consignment">
      <form className="dialog" onSubmit={handleSubmit}>
        <h3>Record returns &amp; close out</h3>
        <p className="dialog-sub">
          {invoice.invoice_number} · {invoice.bill_to_name}
        </p>
        <p className="dialog-note">
          Anything that didn't sell should come back. Enter how many of each you have back in hand —
          they'll be added to your own stock again.
        </p>

        <table className="sales-entry">
          <thead>
            <tr>
              <th>Product</th>
              <th>Sent</th>
              <th>Sold</th>
              <th>Returned</th>
              <th>Still out</th>
            </tr>
          </thead>
          <tbody>
            {preview.map((row) => (
              <tr key={row.itemId} className={row.tooMany ? 'invalid' : undefined}>
                <td>{row.description}</td>
                <td className="numeric">{row.sent}</td>
                <td className="numeric">{row.soldToDate}</td>
                <td>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={returns[row.itemId] || ''}
                    onChange={(e) => setReturns((r) => ({ ...r, [row.itemId]: e.target.value }))}
                    aria-label={`Returned: ${row.description}`}
                  />
                </td>
                <td className="numeric">{row.stillOut}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Says what will happen rather than just enabling or disabling a
            button — the two conditions for closing are different problems
            with different fixes. */}
        <div className="closeout-status">
          {stillOut === 0 && outstanding === 0 ? (
            <p className="closeout-ready">
              Everything is accounted for and nothing is owed — this invoice will show as
              Completed.
            </p>
          ) : (
            <ul className="closeout-blockers">
              {stillOut > 0 && (
                <li>
                  {stillOut} {stillOut === 1 ? 'unit' : 'units'} still with the shop — record them
                  as sold or returned to close this.
                </li>
              )}
              {outstanding > 0 && (
                <li>{formatJMD(outstanding)} of sold stock is still unpaid.</li>
              )}
            </ul>
          )}
        </div>

        {error && <p className="po-error">{error}</p>}

        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="primary-action" disabled={saving || invalid}>
            {saving ? 'Saving…' : 'Save returns'}
          </button>
        </div>
      </form>
    </div>
  );
}
